import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { analysisSessionQueryOptions } from "@/hooks/use-analysis";
import type { AnalysisSessionMessageRaw } from "@/types/analysis";

interface Props {
  sessionId: string | null;
  highlightMessageId?: string | null;
  onOpenChange: (open: boolean) => void;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString("ko-KR", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function MessageBubble({
  msg,
  isHighlighted,
}: {
  msg: AnalysisSessionMessageRaw;
  isHighlighted: boolean;
}) {
  const role = msg.original.role;
  const isAssistant = role === "assistant";
  const analysis = msg.analysis;
  const isNegative = !!analysis?.negativeFeedbackLabel;

  return (
    <div
      data-message-id={msg.original._id}
      className={[
        "rounded-md p-3 text-sm whitespace-pre-wrap",
        isNegative
          ? "bg-red-500/8 border border-red-500/30"
          : isAssistant
            ? "bg-muted/50"
            : "bg-background border",
        isHighlighted ? "border-l-4 border-l-amber-500 bg-amber-500/5" : "",
      ].join(" ")}
    >
      <div className="mb-1 flex items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">
          {role === "assistant" ? "AI" : role === "user" ? "유저" : role}
        </span>
        <span className="ml-auto text-xs text-muted-foreground">
          {formatTime(msg.original.createdAt)}
        </span>
      </div>
      <p>{msg.original.content}</p>
      {analysis && (
        <div className="mt-2 flex flex-wrap gap-1 border-t border-border/60 pt-2">
          {analysis.intentLabelKo && (
            <Badge variant="outline" className="text-xs">
              {analysis.intentLabelKo}
            </Badge>
          )}
          {analysis.negativeFeedbackLabel && (
            <Badge variant="destructive" className="text-xs">
              {analysis.negativeFeedbackLabel}
            </Badge>
          )}
          {analysis.positiveFeedbackLabel && (
            <Badge className="bg-emerald-500/15 text-emerald-700 text-xs hover:bg-emerald-500/20">
              {analysis.positiveFeedbackLabel}
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}

export function SessionDrawer({
  sessionId,
  highlightMessageId,
  onOpenChange,
}: Props) {
  const open = sessionId !== null;
  const query = useQuery(analysisSessionQueryOptions(sessionId));
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open || !highlightMessageId || !query.data) return;
    // cached query.data 로 재오픈 시 mount/transition 직후 DOM 안정화를 1 frame 기다린다.
    const raf = requestAnimationFrame(() => {
      const el = containerRef.current?.querySelector(
        `[data-message-id="${highlightMessageId}"]`,
      );
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(raf);
  }, [open, highlightMessageId, query.data]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full md:max-w-[min(820px,68vw)] overflow-y-auto px-6 py-6"
      >
        <SheetHeader>
          <SheetTitle>세션 전체 보기</SheetTitle>
          <SheetDescription>
            세션 내 모든 메시지와 라벨을 확인합니다.
          </SheetDescription>
        </SheetHeader>

        <div ref={containerRef} className="mt-4 space-y-3">
          {query.isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          )}

          {query.isError && !query.data && (
            <div className="rounded-md border p-4 text-sm text-destructive">
              세션을 불러오지 못했습니다.
              <Button
                size="sm"
                variant="outline"
                className="ml-2"
                onClick={() => query.refetch()}
              >
                다시 시도
              </Button>
            </div>
          )}

          {query.data && (
            <>
              <p className="text-xs text-muted-foreground">
                {query.data.session.sessionType ?? "세션"} ·{" "}
                {query.data.messages.length.toLocaleString("ko-KR")} 메시지
              </p>
              {query.data.messages.length === 0 ? (
                <p className="rounded-md border p-6 text-center text-sm text-muted-foreground">
                  메시지가 없습니다.
                </p>
              ) : (
                query.data.messages.map((m) => (
                  <MessageBubble
                    key={m.original._id}
                    msg={m}
                    isHighlighted={m.original._id === highlightMessageId}
                  />
                ))
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
